import React, { useState, useEffect } from "react";
import BlockLetter from "./BlockLetter";
import "./Header.css";
import { GithubIcon, WebsiteIcon } from "../GridDisplay/icons";

function mathSquareSpiral(n: number): [number, number] {
  const lowerRoot = Math.floor(Math.sqrt(n));
  const anchor = lowerRoot ** 2;
  let location: [number, number];

  if (lowerRoot % 2 === 0) {
    location = [lowerRoot / -2, lowerRoot / 2];
    location[1] -= Math.min(n - anchor, lowerRoot);
    location[0] += Math.max(n - anchor - lowerRoot, 0);
  } else {
    location = [(lowerRoot - 1) / 2 + 1, (lowerRoot - 1) / -2];
    location[1] += Math.min(n - anchor, lowerRoot);
    location[0] -= Math.max(n - anchor - lowerRoot, 0);
  }
  return location;
}

type LetterMatrix = number[][];

const letterS: LetterMatrix = [
  [1, 1, 1, 1, 1],
  [1, 0, 0, 0, 0],
  [1, 1, 1, 1, 1],
  [0, 0, 0, 0, 1],
  [1, 1, 1, 1, 1],
];
const letterQ: LetterMatrix = [
  [1, 1, 1, 1, 1],
  [1, 0, 0, 0, 1],
  [1, 0, 0, 0, 1],
  [1, 0, 0, 1, 1],
  [1, 1, 1, 1, 1],
];
const letterU: LetterMatrix = [
  [1, 0, 0, 0, 1],
  [1, 0, 0, 0, 1],
  [1, 0, 0, 0, 1],
  [1, 0, 0, 0, 1],
  [1, 1, 1, 1, 1],
];
const letterA: LetterMatrix = [
  [1, 1, 1, 1, 1],
  [1, 0, 0, 0, 1],
  [1, 1, 1, 1, 1],
  [1, 0, 0, 0, 1],
  [1, 0, 0, 0, 1],
];
const letterR: LetterMatrix = [
  [1, 1, 1, 1, 1],
  [1, 0, 0, 0, 1],
  [1, 1, 1, 1, 1],
  [1, 0, 0, 1, 0],
  [1, 0, 0, 0, 1],
];
const letterE: LetterMatrix = [
  [1, 1, 1, 1, 1],
  [1, 0, 0, 0, 0],
  [1, 1, 1, 0, 0],
  [1, 0, 0, 0, 0],
  [1, 1, 1, 1, 1],
];
const letterP: LetterMatrix = [
  [1, 1, 1, 1, 1],
  [1, 0, 0, 0, 1],
  [1, 1, 1, 1, 1],
  [1, 0, 0, 0, 0],
  [1, 0, 0, 0, 0],
];
const letterI: LetterMatrix = [
  [1, 1, 1, 1, 1],
  [0, 0, 1, 0, 0],
  [0, 0, 1, 0, 0],
  [0, 0, 1, 0, 0],
  [1, 1, 1, 1, 1],
];
const letterL: LetterMatrix = [
  [1, 0, 0, 0, 0],
  [1, 0, 0, 0, 0],
  [1, 0, 0, 0, 0],
  [1, 0, 0, 0, 0],
  [1, 1, 1, 1, 1],
];

const initSquarray: LetterMatrix = [
  [0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0],
];

const Header: React.FC = () => {
  const [index, setIndex] = useState(0);
  const [squarray, setSquarray] = useState<LetterMatrix>(initSquarray);
  const origin = 2;

  function reset() {
    if (index !== -1) return;
    setSquarray([
      [0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0],
    ]);
    setIndex(0);
  }

  function iterateFastSquareSpiral(originIndex: number) {
    const [ix, iy] = mathSquareSpiral(index);
    const [x, y] = [ix + originIndex, originIndex + iy];
    const tempSquarray = squarray.map((row) => [...row]);
    if (squarray[y] && typeof squarray[y][x] !== "undefined") {
      tempSquarray[y][x] = 1;
    }
    setSquarray(tempSquarray);
    setIndex(index + 1);
  }

  useEffect(() => {
    if (index < 25 && index >= 0) {
      const timeout = setTimeout(() => {
        iterateFastSquareSpiral(origin);
      }, 110);
      return () => clearTimeout(timeout);
    } else if (index >= 25) {
      setIndex(-1);
    }
  }, [index]);

  return (
    <div onClick={reset} className="Header">
      <div className="HeaderRow">
        <BlockLetter index={index} squares={squarray} letter={letterS} first />
        <BlockLetter index={index} squares={squarray} letter={letterQ} />
        <BlockLetter index={index} squares={squarray} letter={letterU} />
        <BlockLetter index={index} squares={squarray} letter={letterA} />
        <BlockLetter index={index} squares={squarray} letter={letterR} />
        <BlockLetter index={index} squares={squarray} letter={letterE} />
      </div>
      <div className="HeaderRow">
        <BlockLetter index={index} squares={squarray} letter={letterS} first />
        <BlockLetter index={index} squares={squarray} letter={letterP} />
        <BlockLetter index={index} squares={squarray} letter={letterI} />
        <BlockLetter index={index} squares={squarray} letter={letterR} />
        <BlockLetter index={index} squares={squarray} letter={letterA} />
        <BlockLetter index={index} squares={squarray} letter={letterL} />
        <BlockLetter index={index} squares={squarray} letter={letterS} />
      </div>
      <div className="links">
        <GithubIcon ic="linkIcon" />
        <WebsiteIcon ic="linkIcon" />
      </div>
    </div>
  );
};

export default Header;


